import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  AuditOutlined,
  BarcodeOutlined,
  CheckCircleOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  ContainerOutlined,
  DeploymentUnitOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  ExportOutlined,
  FileDoneOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  InboxOutlined,
  RollbackOutlined,
  SafetyCertificateOutlined,
  ShoppingCartOutlined,
  SolutionOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { ReactNode } from 'react';
import { PageHeader } from '../components/PageHeader';
import { api, unwrap } from '../api/client';
import type { components } from '../api/schema';

type Stage = components['schemas']['ProcessStageDto'];

/**
 * The whole Tech. Supply process on one page, in the order a part travels through it.
 *
 * Six departmental dashboards answered "how is procurement doing"; nobody was answering the question a storekeeper
 * actually has on arriving — what is waiting, and where do I go. Each stage prints what is waiting at it and opens the
 * screen that holds the work. A stage the reader may not open is still drawn, greyed: the department does that work
 * whether or not this person does, and a hole in the picture would misdescribe the process.
 */
const ICONS: Readonly<Record<string, ReactNode>> = {
  demand: <FileTextOutlined />,
  rfq: <SolutionOutlined />,
  quotation: <FileSearchOutlined />,
  purchaseOrder: <ShoppingCartOutlined />,
  receiving: <InboxOutlined />,
  inspection: <SafetyCertificateOutlined />,
  putAway: <DownloadOutlined />,
  discrepancy: <ExclamationCircleOutlined />,
  stock: <ContainerOutlined />,
  belowMin: <WarningOutlined />,
  issue: <ExportOutlined />,
  aog: <ThunderboltOutlined />,
  returns: <RollbackOutlined />,
  count: <BarcodeOutlined />,
  disposition: <FileDoneOutlined />,
  repair: <ToolOutlined />,
  atVendor: <ClockCircleOutlined />,
  warranty: <AuditOutlined />,
  loans: <DeploymentUnitOutlined />,
  calibration: <CheckCircleOutlined />,
  approvals: <CheckSquareOutlined />,
};

/**
 * The four bands a part actually passes through, in order, and then the signing lane. The four are numbered because
 * they genuinely are a sequence; "Sign" is not a fifth step but a lane that runs alongside all of them, so it carries
 * no number — a number there would assert an order that does not exist.
 */
const SEQUENCE = ['acquire', 'receive', 'hold', 'restore'] as const;
const BANDS = [...SEQUENCE, 'sign'] as const;

/** Where a band sits in the sequence, or undefined for the lane that is not part of it. */
const step = (band: string): number | undefined => {
  const at = SEQUENCE.indexOf(band as (typeof SEQUENCE)[number]);
  return at < 0 ? undefined : at + 1;
};

export default function Flow() {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ['process-flow'],
    queryFn: () => unwrap(api.GET('/api/v1/rpt/flow', {})),
    staleTime: 30_000,
  });

  const stages = query.data?.stages ?? [];
  const bands = BANDS.map((band) => ({ band, stages: stages.filter((s) => s.band === band) })).filter((b) => b.stages.length > 0);

  return (
    <>
      <PageHeader title={t('flow.title')} subtitle={t('flow.sub')} />
      {query.isError && <p className="muted card-gap">{t('flow.unavailable')}</p>}
      <div className="flow">
        {bands.map(({ band, stages: inBand }) => (
          <section key={band} className="flow-band" aria-labelledby={`band-${band}`}>
            <h2 id={`band-${band}`} className="flow-band-title">
              {step(band) && <span className="flow-step">{step(band)}</span>}
              {t(`flow.band.${band}`)}
            </h2>
            <div className="flow-stages">
              {inBand.map((stage) => (
                <StageTile key={stage.key} stage={stage} loading={query.isLoading} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </>
  );
}

function StageTile({ stage, loading }: { stage: Stage; loading: boolean }) {
  const { t } = useTranslation();
  const label = t(`flow.stage.${stage.key}`);
  const figure = loading ? '…' : stage.waiting === null ? '—' : String(stage.waiting);
  // a stage with nothing waiting is drawn quietly: the eye should land on the queues that need someone
  const tone = stage.waiting ? stage.tone ?? undefined : undefined;
  const body = (
    <>
      <span className="ico" aria-hidden="true">
        {ICONS[stage.key] ?? <FileTextOutlined />}
      </span>
      <b className="num">{figure}</b>
      <span className="lbl">{label}</span>
    </>
  );

  if (!stage.allowed) {
    return (
      <div className="flow-tile locked" title={t('flow.stage.locked')} aria-label={`${label} — ${t('flow.stage.locked')}`}>
        {body}
      </div>
    );
  }
  return (
    <Link className={`flow-tile${tone ? ` ${tone}` : ''}${stage.waiting === 0 ? ' quiet' : ''}`} to={stage.route}>
      {body}
    </Link>
  );
}
